import { useEffect, useState } from "react";
import { LicenseDaysToExpiration } from "../LicenseDaysToExpiration/LicenseDaysToExpiration";
import { BillingPeriodText, DiscountType } from "../../constants/billing";
import { formatMoney } from "../../utils/number";
import { concatTime, formatDate } from "../../utils/date";
import { LicenseBillingStatus as LicenseBillingStatusEnum, LicensesBillingStatuses } from "../../constants/licenses";
import { LicenseBillingStatus } from "../LicenseBillingStatus/LicenseBillingStatus";
import List from "../List/List";
import Pagination from "../Pagination/Pagination";
import LicenseService from "../../services/LicensesService";

export function LicenseBillingPage({ license }) {
   const [billings, setBillings] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [error, setError] = useState("");
   const [page, setPage] = useState(1);
   const [itemsPerPage, setItemsPerPage] = useState(6);
   const [pagination, setPagination] = useState({
      currentPage: 1,
      totalItems: 0,
      totalPages: 0,
   });

   useEffect(() => {
      let isCurrentRequest = true;

      async function fetchBillings() {
         setIsLoading(true);
         setError("");

         const response = await LicenseService.getPaginatedLicenseBillings(
            license.id,
            page,
            itemsPerPage,
         );

         if (!isCurrentRequest) return;

         if (response?.status >= 200 && response.status < 300) {
            const result = response.data;
            const pageInfo = result.pagination ?? {};
            setBillings(result.items ?? []);
            setPagination({
               currentPage: pageInfo.currentPage ?? page,
               totalItems: pageInfo.totalItems ?? 0,
               totalPages: pageInfo.totalPages ?? 0,
            });
         } else {
            setBillings([]);
            setPagination({ currentPage: page, totalItems: 0, totalPages: 0 });
            setError(
               response?.data?.message ??
               "Não foi possível carregar as cobranças desta licença.",
            );
         }
         setIsLoading(false);
      }

      fetchBillings();
      return () => {
         isCurrentRequest = false;
      };
   }, [license.id, page, itemsPerPage]);

   const billing = license.currentBilling &&
      (license.currentBilling.status !== LicenseBillingStatusEnum.PAID || !license.nextBilling)
      ? license.currentBilling
      : license.nextBilling;

   const columns = [
      { collumnLabel: "Vencimento", index: "dueDate", type: "date" },
      { collumnLabel: "Valor", index: "finalAmount", type: "money" },
      {
         forceLabel: true,
         collumnLabel: "Status",
         index: "status",
         type: "custom",
         custom: ({ row }) => {
            const status = LicensesBillingStatuses[row.status];
            const dueDate = new Date(concatTime(row.dueDate));
            const paidAt = row.payments
               .filter(p => p.status === LicenseBillingStatusEnum.PAID)
               .map(p => new Date(p.paidAt))
               .sort((a, b) => b - a);
            const paidLate = paidAt?.[0] && paidAt[0].getTime() > dueDate.getTime();

            return (
               <span
                  className="font-semibold"
                  style={{ color: status?.color }}
               >
                  {paidLate ? 'Pago com atraso' : (status?.name ?? row.status)}
               </span>
            );
         },
      },
      {
         collumnLabel: "Período",
         index: "period",
         type: "custom",
         custom: ({ row }) => `${BillingPeriodText[row.period] ?? row.period}`,
      },
      {
         collumnLabel: "Desconto",
         index: "discount",
         type: "custom",
         custom: ({ row }) => {
            if (!row.discount) return <span className="opacity-40">—</span>;
            return (
               <span className="inline-flex gap-1 flex-col">
                  <span>{row.discountType === DiscountType.Percentage ? `${formatMoney(row.discount * 100)}%` : `R$ ${formatMoney(row.discount)}`}</span>
                  {row.discountReason && <span className="opacity-40 text-xs">({row.discountReason})</span>}
               </span>
            );
         },
      },
   ];

   return (
      <div className="fade-in">
         {billing && (
            <div className="p-5! border! border-(--borderColor)! rounded-(--borderRadius)!">
               <h3 className="font-bold! mb-5!">Situação financeira atual</h3>
               <div className="grid grid-cols-3 gap-x-12! gap-y-4! mt-2!">
                  <div className="flex flex-col gap-2!">
                     <span className="opacity-60">Status</span>
                     <span className="[&_svg]:hidden! inline-flex flex-col gap-y-1!">
                        <LicenseBillingStatus
                           className="p-0! **:text-2xl!"
                           license={license}
                           withBackground={false}
                        />
                        {license.currentBilling?.status !== "PAID" && new Date(concatTime(license.currentBilling.dueDate)) < new Date() && (
                           <LicenseDaysToExpiration
                              className="inline-flex opacity-65 **:text-[14px]!"
                              license={license}
                           />
                        )}
                     </span>
                  </div>
                  <div className="flex flex-col gap-1.5!">
                     <span className="opacity-60">Próximo vencimento</span>
                     <span className="inline-flex mt-1.5! text-[1.0rem]! font-bold!">{formatDate(license.nextDueDate, false)}</span>
                     <LicenseDaysToExpiration
                        className="inline-flex mt-0.5! opacity-65 **:text-[14px]!"
                        license={license}
                        dueDate={license.nextDueDate}
                     />
                  </div>
                  <div className="flex flex-col gap-1.5!">
                     <span className="opacity-60">Valor</span>
                     <span className="inline-flex mt-1.5! text-[1.0rem]! font-bold!">R$ {formatMoney(billing?.finalAmount)}</span>
                     <span className="inline-flex mt-0.5! opacity-65 text-[14px]!">
                        {BillingPeriodText[billing.period]}
                     </span>
                  </div>
               </div>
            </div>
         )}
         <section className="mt-8!">
            <h3 className="font-bold! mb-4!">Histórico de cobranças</h3>
            {error && (
               <p className="mb-3! text-red-500" role="alert">{error}</p>
            )}
            <List
               data={billings}
               collumsObjIndex={columns}
               isLoading={isLoading}
               selectableRows={false}
            />
            <Pagination
               currentPage={pagination.currentPage}
               maxPage={pagination.totalPages}
               rowsPerPage={itemsPerPage}
               total={pagination.totalItems}
               changePage={setPage}
               changeRowsPerPage={(value) => {
                  setPage(1);
                  setItemsPerPage(value);
               }}
            />
         </section>
      </div>
   );
}